import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Pressable, xcss } from '@atlaskit/primitives';

const i18n = defineMessages({
	groupOverflowLabel: {
		id: 'team-presence.group.overflow.label',
		defaultMessage: 'Overflow menu',
		description: 'Accessible label for presence overflow menu button',
	},
});

const moreIndicatorButtonStyle = xcss({
	minWidth: '28px',
	padding: 'space.0',
	margin: 'space.025',
	backgroundColor: 'color.background.neutral.subtle',
	borderRadius: 'border.radius.100',
	':focus-visible': {
		outlineColor: 'color.border.focused',
		outlineStyle: 'solid',
		outlineWidth: 'border.width.outline',
		outlineOffset: 'space.050',
	},
});

const moreIndicatorStyle = xcss({
	padding: 'space.075',
	font: 'font.heading.xxsmall',
	color: 'color.text.subtle',
	backgroundColor: 'color.background.accent.gray.subtler',
	borderRadius: 'border.radius.400',
	':hover': {
		backgroundColor: 'color.background.accent.gray.subtler.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.gray.subtler.pressed',
	},
});

const moreIndicatorActiveStyle = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

export function renderMoreIndicator(isOpen: boolean, count: number) {
	return (_: any, props: any) => {
		return <CorePresenceMoreIndicator count={count} isOpen={isOpen} {...props} />;
	};
}

export type CorePresenceMoreIndicatorProps = {
	count: number;
	isOpen: boolean;
	onClick?: () => void;
	testId?: string;
};

export const CorePresenceMoreIndicator = ({
	count,
	isOpen,
	onClick,
	testId,
}: CorePresenceMoreIndicatorProps) => {
	const { formatMessage } = useIntl();

	return (
		<Pressable
			data-vc="presence-overflow"
			aria-label={formatMessage(i18n.groupOverflowLabel)}
			onClick={onClick}
			testId={testId}
			xcss={moreIndicatorButtonStyle}
		>
			<Box xcss={[moreIndicatorStyle, isOpen && moreIndicatorActiveStyle]}>{count}</Box>
		</Pressable>
	);
};
